import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { ConciliacaoResponseDto, ConciliacaoFilterDto } from '@/services/conciliacao-fiscal';
import { useState } from 'react';
import { Clock, Loader2 } from 'lucide-react';
import DetailedAnalysisSection from './detailed-analysis-section';
import ExportDialog from './export-dialog';

interface ResultsSectionProps {
  data?: ConciliacaoResponseDto;
  onPageChange?: (page: number) => void;
  loading?: boolean;
  currentFilters?: ConciliacaoFilterDto;
}

export default function ResultsSection({ data, onPageChange, loading = false, currentFilters }: ResultsSectionProps) {
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Loading state - show spinner when loading but no data yet
  if (loading && !data) {
    return (
      <Card className="p-8 text-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">
            Executando conciliação fiscal...
          </p>
          <p className="text-sm text-muted-foreground">
            Este processo pode levar alguns minutos
          </p>
        </div>
      </Card>
    );
  }

  // Empty state - no loading, no data
  if (!data) {
    return (
      <Card className="p-8 text-center">
        <div className="flex flex-col items-center space-y-2">
          <div className="text-6xl mb-2">📊</div>
          <p className="text-muted-foreground font-medium">
            Execute uma conciliação para visualizar os resultados
          </p>
          <p className="text-sm text-muted-foreground">
            Configure os filtros acima e clique em "Executar Conciliação"
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Loading overlay when paginating */}
      {loading && data && (
        <Card className="p-4 border-l-4 border-l-primary bg-primary/5">
          <div className="flex items-center space-x-3">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <p className="text-sm text-primary font-medium">
              Carregando nova página...
            </p>
          </div>
        </Card>
      )}

      {/* Detailed Analysis Section */}
      {data && (
        <DetailedAnalysisSection
          data={data}
          onPageChange={onPageChange}
          loading={loading}
          currentFilters={currentFilters}
        />
      )}

      {/* Processing time footer */}
      {data.executadoEm && (
        <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>
            Executado em {new Date(data.executadoEm).toLocaleString('pt-BR')}
            {data.tempoProcessamento !== undefined && (
              <Badge variant="outline" className="ml-2 font-mono text-xs">
                {data.tempoProcessamento < 60
                  ? `${data.tempoProcessamento}s`
                  : `${Math.floor(data.tempoProcessamento / 60)}m ${data.tempoProcessamento % 60}s`}
              </Badge>
            )}
          </span>
        </div>
      )}

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        data={data}
        currentFilters={currentFilters}
        section="summary"
      />
    </div>
  );
}